import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { ListingSeo } from '../../domain/models/Seo';
import { isMultiStoreCatalogRoute } from '@/features/plp/utils/plpUrl';
import { normalizeBluestoneCanonical } from '@/features/plp/utils';

/**
 * Sets document head from browse API SEO (title, description, canonical, keywords, robots, schema, og:url).
 * Runs on client; SSR injects the same via htmlTransforms when plpData.seo is present.
 *
 * Simplified so that we NEVER actively clear description/OG/Twitter tags on React navigation.
 * We only overwrite when new values are present, so SSR SEO stays until new SEO arrives.
 */
export function PlpSeoHead({ seo, redirectUrl }: { seo: ListingSeo | null | undefined; redirectUrl?: string | null }) {
  const location = useLocation();
  // Track the last seo canonical/title used to set document.title so we can detect
  // when genuinely new seo arrives (not just a pathname change with stale seo).
  const titleSetForSeoRef = useRef<string | null>(null);

  useEffect(() => {
    if (!seo) {
      // No new SEO yet – keep existing description and OG/Twitter tags injected by SSR.
      return;
    }

    const isMultiStorePage = isMultiStoreCatalogRoute(location.pathname);
    // Force noindex when the API returned a redirectUrl that differs from the current path;
    // this page is non-canonical and should not be indexed.
    const isNonCanonical =
      typeof redirectUrl === 'string' &&
      redirectUrl !== '' &&
      redirectUrl.split('?')[0] !== location.pathname;
    // Mirror injectRouteSeo.ts: only explicit production deploy is indexable.
    const deployEnv = ((import.meta.env.VITE_DEPLOY_ENV as string) ?? '').toLowerCase().trim();
    const allowIndex = deployEnv === 'production' && import.meta.env.MODE !== 'development';
    const robotsContent =
      !allowIndex || isNonCanonical || isMultiStorePage ? 'noindex, nofollow' : (seo.robotsMeta || 'index, follow');
    const titleContent = seo.title || seo.header || 'BlueStone';

    // Update document.title whenever the seo identity changes (new page, new canonical)
    // or whenever the pathname changes with fresh seo (catches SPA navigation).
    // We key on the seo canonical (or title) so that stale seo from a previous page
    // cannot lock out the correct seo that arrives later for the new page.
    const seoKey = `${location.pathname}::${seo.canonical ?? ''}::${titleContent}`;
    if (titleSetForSeoRef.current !== seoKey) {
      document.title = titleContent;
      titleSetForSeoRef.current = seoKey;
    }

    // Only set these when API sends a value; otherwise leave previous meta as-is.
    if (seo.description) {
      setMeta('description', seo.description);
      setMeta('og:description', seo.description, 'property');
      setMeta('twitter:description', seo.description);
    }
    if (seo.keywords) {
      setMeta('keywords', seo.keywords);
    }

    setMeta('robots', robotsContent);

    const canonicalSource = seo.canonical || location.pathname;
    setCanonical(normalizeBluestoneCanonical(canonicalSource));

    if (seo.shareUrl) {
      setMeta('og:url', seo.shareUrl, 'property');
      setMeta('twitter:url', seo.shareUrl);
    }

    setMeta('og:title', titleContent, 'property');
    setMeta('twitter:title', titleContent);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@bluestone_com');

    const normalizePagination = (raw: string) => {
      try {
        const u = new URL(raw, 'https://www.bluestone.com');
        return `https://www.bluestone.com${u.pathname}${u.search}`;
      } catch { return raw; }
    };
    setPaginationLink('prev', seo.prev ? normalizePagination(seo.prev) : undefined);
    setPaginationLink('next', seo.next ? normalizePagination(seo.next) : undefined);

    if (seo.schema != null) {
      setSchema(seo.schema);
    }
  }, [
    location.pathname,
    redirectUrl,
    seo,
    seo?.title,
    seo?.header,
    seo?.description,
    seo?.keywords,
    seo?.robotsMeta,
    seo?.canonical,
    seo?.shareUrl,
    seo?.schema,
    seo?.prev,
    seo?.next,
  ]);

  return null;
}

function setMeta(nameOrProperty: string, content: string, attr: 'name' | 'property' = 'name') {
  const selector = attr === 'property' ? `meta[property="${nameOrProperty}"]` : `meta[name="${nameOrProperty}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, nameOrProperty);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function setSchema(schema: unknown) {
  let script = document.querySelector('script[data-plp-schema="true"]');
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-plp-schema', 'true');
    document.head.appendChild(script);
  }
  script.textContent = (typeof schema === 'string' ? schema : JSON.stringify(schema)).replace(/__BASE_URL__/g, window.location.origin);
}

function setPaginationLink(rel: 'prev' | 'next', href: string | undefined) {
  const existing = document.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    existing?.remove();
    return;
  }
  let link = existing;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}
